import { useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import {
  ASSIGNABLE_WELLSPRING_ROLES,
  type AssignableWellspringRole,
  canManageWellspringRole,
  isAssignableWellspringRole,
  toErrorMessage,
  wellspringRoleRank,
  type WellspringUser,
} from '@metron/shared';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useWellspringUsers } from '../hooks/useWellspringUsers.ts';
import { useTableUrlState } from '../hooks/useTableUrlState.ts';
import { DataTable } from '../components/data-table/data-table.tsx';
import { DataTableColumnHeader } from '../components/data-table/data-table-column-header.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';

/** Select needs a non-empty value; this stands in for "no role". */
const NO_ROLE = 'none';

const DEFAULT_SORTING = [{ id: 'email', desc: false }];

type Role = AssignableWellspringRole | null;
const roleLabel = (role: Role) => role ?? 'Viewer';

interface RoleChange {
  user: WellspringUser;
  to: Role;
}

/**
 * Wellspring Access: assign Wellspring's own roles (Support / Admin / SuperAdmin)
 * to users in Wellspring's auth pool. Users with no role are implicit Viewers
 * via the metron-internal group. Callers only manage roles below their own,
 * mirroring the API check.
 *
 * Edits are staged: changing a row marks it unsaved, and nothing is written
 * until the caller reviews the full list of changes and confirms.
 */
export function WellspringAccessPage() {
  const { user: me } = useAuth();
  const myGroups = me?.groups ?? [];
  const myEmail = me?.email?.toLowerCase();
  const { users, isLoading, error, setRole } = useWellspringUsers();
  const tableState = useTableUrlState({ defaultSorting: DEFAULT_SORTING });

  /** Staged role per username; absent means unchanged. */
  const [pending, setPending] = useState<Record<string, Role>>({});
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const changes: RoleChange[] = users.flatMap((user) =>
    user.username in pending ? [{ user, to: pending[user.username] }] : []
  );

  function stage(u: WellspringUser, value: string) {
    let role: Role = null;
    if (value !== NO_ROLE) {
      if (!isAssignableWellspringRole(value)) return;
      role = value;
    }
    setPending((prev) => {
      const next = { ...prev };
      if (role === u.role) delete next[u.username];
      else next[u.username] = role;
      return next;
    });
  }

  async function apply() {
    setSaving(true);
    let applied = 0;
    for (const { user, to } of changes) {
      try {
        await setRole.mutateAsync({ username: user.username, role: to });
        setPending((prev) => {
          const next = { ...prev };
          delete next[user.username];
          return next;
        });
        applied++;
      } catch (e) {
        // Leave the failed row staged so the caller can retry or discard it.
        toast.error(`${user.email}: ${toErrorMessage(e)}`);
      }
    }
    if (applied > 0) toast.success(`Updated ${applied} role${applied === 1 ? '' : 's'}`);
    setSaving(false);
    setConfirming(false);
  }

  const columns: ColumnDef<WellspringUser>[] = [
    {
      accessorKey: 'email',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Email' />,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Name' />,
    },
    {
      id: 'role',
      // Sort by privilege, not alphabetically, so Viewer < Support < Admin < SuperAdmin.
      accessorFn: (u) => wellspringRoleRank(u.role),
      header: ({ column }) => <DataTableColumnHeader column={column} title='Role' />,
      cell: ({ row }) => {
        const u = row.original;
        const isMe = !!myEmail && u.email.toLowerCase() === myEmail;
        const options = ASSIGNABLE_WELLSPRING_ROLES.filter((r) =>
          canManageWellspringRole(myGroups, u.role, r)
        );
        const editable = !isMe && canManageWellspringRole(myGroups, u.role, null);
        const staged = u.username in pending;
        const shown = staged ? pending[u.username] : u.role;
        return (
          <div className='flex items-center gap-2'>
            <Select
              value={shown ?? NO_ROLE}
              onValueChange={(v) => stage(u, v)}
              disabled={!editable || saving}
            >
              <SelectTrigger aria-label={`Role for ${u.email}`} className='w-44'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_ROLE}>Viewer (no role)</SelectItem>
                {/* Keep the current role visible even when the caller can't assign it. */}
                {u.role && !options.includes(u.role) && (
                  <SelectItem value={u.role}>{u.role}</SelectItem>
                )}
                {options.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            {staged && <Badge variant='outline'>Unsaved</Badge>}
          </div>
        );
      },
    },
  ];

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle>Wellspring Access</CardTitle>
          <CardDescription>
            Assign Wellspring roles. Users without a role have Viewer access. You can only manage
            roles below your own, and not your own. Changes take effect when you save them.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {isLoading && <p className='text-muted-foreground text-sm'>Loading…</p>}
          {error && <p className='text-destructive text-sm'>{toErrorMessage(error)}</p>}
          {!isLoading && !error && (
            <DataTable
              columns={columns}
              data={users}
              sorting={tableState.sorting}
              onSortingChange={tableState.onSortingChange}
              pagination={tableState.pagination}
              onPaginationChange={tableState.onPaginationChange}
            />
          )}
          {changes.length > 0 && (
            <div className='flex items-center justify-end gap-3'>
              <span className='text-muted-foreground text-sm'>
                {changes.length} unsaved change{changes.length === 1 ? '' : 's'}
              </span>
              <Button variant='outline' onClick={() => setPending({})} disabled={saving}>
                Discard
              </Button>
              <Button onClick={() => setConfirming(true)} disabled={saving}>
                Save changes
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirming} onOpenChange={(open) => !saving && setConfirming(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply role changes?</AlertDialogTitle>
            <AlertDialogDescription>
              These roles take effect at each user's next sign-in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className='space-y-1 text-sm'>
            {changes.map(({ user, to }) => (
              <li key={user.username}>
                <span className='font-medium'>{user.email}</span>: {roleLabel(user.role)} →{' '}
                {roleLabel(to)}
              </li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => (e.preventDefault(), apply())} disabled={saving}>
              {saving
                ? 'Applying…'
                : `Apply ${changes.length} change${changes.length === 1 ? '' : 's'}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
